<template>
  <church-updates-layout :enable-scroll-event="true">
    <div class="flex flex-col w-full h-full px-[20px] bg-[#EAE8E5] pb-[64px]">
      <div class="read-more-header pb-[16px] flex items-center justify-between">
        <BackButton fallback-route="/church-updates" disable-safe-area compact />
        <button
          v-if="canShareSelectedArticle"
          class="flex items-center justify-center w-[44px] h-[44px] rounded-full active:bg-[#D5CFC8]"
          @click="shareUpdate"
        >
          <ShareIcon fill="#63615E" />
        </button>
      </div>
      <div
        v-show="!isScrolled"
        class="flex flex-row flex-wrap text-[0.75rem] leading-[1rem] font-semibold"
      >
        <div
          v-if="isWithinNewPeriod(selectedArticle)"
          class="bg-[#FED970] text-[#8C6D17] rounded-[16px] items-center text-center h-[24px] px-2 py-1 mr-2 whitespace-nowrap"
        >
          New
        </div>
        <div
          v-for="(tag, index) in selectedArticle?.church_update_tags"
          :key="index"
          :style="setTagColor(tag.backgroundColor, tag.textColor)"
          class="rounded-[16px] px-2 py-1 mr-2"
        >
          {{ tag.name }}
        </div>
      </div>
      <!-- Scrollable region with Storyblok content -->
      <div
        class="flex-1 min-h-0 overflow-auto no-scrollbar pb-[20px] bg-[#EAE8E5] relative"
        @scroll="handleScroll"
      >
        <LoadingState v-if="isLoading || !storyContent" />
        <div v-if="storyContent && !isLoading" class="pt-4">
          <StoryblokRenderer :content="storyContent" />
        </div>
        <div v-if="error && !isLoading" class="pt-4 text-center text-[#63615E]">
          <p>{{ error }}</p>
        </div>
      </div>
    </div>
  </church-updates-layout>
</template>

<script>
import { Share } from '@capacitor/share';
import { mapState } from 'vuex';
import BackButton from '../../components/common/BackButton.vue';
import LoadingState from '../../components/common/LoadingState.vue';
import ShareIcon from '../../components/common/ShareIcon.vue';
import ChurchUpdatesLayout from '../../components/ChurchUpdatesLayout.vue';
import StoryblokRenderer from '../../components/ChurchUpdatesPage/StoryblokRenderer.vue';
import { getStory } from '@/utils/storyblok';
import { getErrorMessage } from '@/utils/error';
import { canShareChurchUpdate, isWithinNewPeriod } from '@/utils/church-updates';
import { useFeatureFlags } from '@/composables/useFeatureFlags';

export default {
  components: {
    BackButton,
    LoadingState,
    ShareIcon,
    ChurchUpdatesLayout,
    StoryblokRenderer,
  },
  setup() {
    const { isEnabled } = useFeatureFlags();
    return { isFeatureFlagEnabled: isEnabled };
  },
  data() {
    return {
      selectedArticle: null,
      storyContent: null,
      isLoading: true,
      error: null,
      isScrolled: false,
    };
  },
  computed: {
    ...mapState('churchUpdates', ['churchUpdates', 'isLoading', 'selectedArticleIndex']),
    canShareSelectedArticle() {
      return (
        Boolean(this.selectedArticle) &&
        this.isFeatureFlagEnabled('share-links') &&
        canShareChurchUpdate(this.selectedArticle)
      );
    },
  },
  methods: {
    isWithinNewPeriod,
    setTagColor(backgroundColor, textColor) {
      return {
        backgroundColor: backgroundColor,
        color: textColor,
      };
    },
    async shareUpdate() {
      if (!this.selectedArticle || !canShareChurchUpdate(this.selectedArticle)) return;
      try {
        let url = '';
        try {
          url = await this.$store.dispatch('churchUpdates/generateShareLink', {
            slug: this.selectedArticle.slug,
          });
        } catch {
          // Fallback: share without URL if API fails
        }
        await Share.share({
          title: this.selectedArticle.title,
          text: `Check out "${this.selectedArticle.title}" on the HOGC One App!`,
          url,
          dialogTitle: 'Share Update',
        });
      } catch {
        // User cancelled the share dialog
      }
    },
    handleScroll(event) {
      const scrollTop = event.target.scrollTop;
      this.isScrolled = scrollTop > 0;
    },
    async fetchStoryblokContent(slug) {
      try {
        this.isLoading = true;
        this.error = null;
        this.storyContent = null;

        const isPreview =
          typeof window !== 'undefined' && window.location?.pathname?.startsWith('/preview/');

        if (isPreview) {
          // Preview mode: fetch draft straight from Storyblok so CMS editors
          // can see unpublished edits before they go live.
          const storyData = await getStory(`church-updates/${slug}`);
          const content = storyData?.story?.content || {};
          if (
            this.selectedArticle &&
            Object.prototype.hasOwnProperty.call(content, 'showShareButton')
          ) {
            this.selectedArticle = {
              ...this.selectedArticle,
              showShareButton: canShareChurchUpdate(content),
            };
          }
          const body = content.body || [];
          this.storyContent = Array.isArray(body) ? body : [];
          return;
        }

        // Published mode: fetch via API so Storyblok bandwidth is shared
        // across users via the server-side cache.
        const body = await this.$store.dispatch('churchUpdates/getBodyBySlug', { slug });
        this.storyContent = Array.isArray(body) ? body : [];
      } catch (err) {
        this.error = getErrorMessage(
          err,
          'Failed to load article content. Please try again later.',
        );
      } finally {
        this.isLoading = false;
      }
    },
  },
  async ionViewWillEnter() {
    try {
      await this.$store.dispatch('profile/fetchProfile');
      await this.$store.dispatch('churchUpdates/getChurchUpdates');
      const slug = this.$route.params.slug;

      // Normalize slug from route (remove any path prefix and trailing slashes)
      const normalizedRouteSlug =
        slug && typeof slug === 'string' ? slug.replace(/^.*\//, '').replace(/\/+$/, '') : slug;

      // Find item matching slug in churchUpdates
      // Normalize both sides for comparison - slugs might be full paths like "church-updates/my-slug"
      let foundArticle = null;
      this.churchUpdates.forEach(item => {
        // Normalize item slug the same way as route slug
        const normalizedItemSlug =
          item.slug && typeof item.slug === 'string'
            ? item.slug.replace(/^.*\//, '').replace(/\/+$/, '')
            : item.slug;

        if (normalizedItemSlug === normalizedRouteSlug) {
          foundArticle = item;
        }
      });

      if (!foundArticle) {
        // Article not in PAC-filtered list — user may be accessing via a shared
        // link to content above their access level. Bypass PAC by fetching
        // directly by slug.
        try {
          foundArticle = await this.$store.dispatch('churchUpdates/getUpdateBySlug', {
            slug: normalizedRouteSlug,
          });
        } catch (bypassErr) {
          console.log('Church update bypass fetch failed', {
            routeSlug: slug,
            normalizedRouteSlug,
            error: bypassErr?.message,
          });
        }

        if (!foundArticle) {
          this.$router.push('/church-updates');
          return;
        }
      }

      this.selectedArticle = foundArticle;

      // Fetch content from Storyblok using the normalized slug
      await this.fetchStoryblokContent(normalizedRouteSlug);
    } catch (err) {
      console.error('Error in ionViewWillEnter:', err);
      this.error = 'Failed to load article. Please try again.';
      this.isLoading = false;
    }
  },
};
</script>

<style scoped>
.read-more-header {
  padding-top: env(safe-area-inset-top, 0px);
}

.no-scrollbar::-webkit-scrollbar {
  display: none;
}

.no-scrollbar {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
}
</style>
